import { ALLOWED_REDIRECT_DOMAINS } from '@/infra/constants/domains';

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Only allows relative paths or URLs from allowed domains.
 *
 * @param url - The URL to validate (can be relative or absolute)
 * @param fallback - The fallback URL to return if validation fails (default: '/home'). If null, returns null on validation failure.
 * @returns The validated URL, the fallback if validation fails, or null if no fallback provided and validation fails
 */
export function validateRedirectUrl(url: string | null | undefined, fallback: string): string;
export function validateRedirectUrl(url: string | null | undefined, fallback: null): string | null;
export function validateRedirectUrl(url: string | null | undefined, fallback: string | null = '/home'): string | null {
  // If no URL provided, return fallback (or null if fallback is null)
  if (!url) {
    return fallback ?? null;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  // If it's a relative path (starts with /), it's safe
  if (trimmedUrl.startsWith('/')) {
    // Additional check: ensure it's not a protocol-relative URL (//example.com)
    if (trimmedUrl.startsWith('//')) {
      return fallback ?? null;
    }
    return trimmedUrl;
  }

  // For absolute URLs, check if domain is in allowed list
  try {
    const urlObj = new URL(trimmedUrl);

    // Get allowed domains from constants
    const allowedDomains = ALLOWED_REDIRECT_DOMAINS;

    // Check if the hostname matches any allowed domain
    const isAllowed = allowedDomains.some((domain) => {
      // Handle wildcard patterns (e.g., *.vercel.app)
      if (domain.startsWith('*.')) {
        const baseDomain = domain.slice(2); // Remove '*.'
        // Match any subdomain of the base domain (e.g., my-app.vercel.app matches *.vercel.app)
        // But not the base domain itself (vercel.app does NOT match *.vercel.app)
        if (urlObj.hostname.endsWith(`.${baseDomain}`)) {
          return true;
        }
        return false;
      }

      // Exact match
      if (urlObj.hostname === domain) {
        return true;
      }

      // Subdomain match (e.g., app.example.com matches example.com)
      if (urlObj.hostname.endsWith(`.${domain}`)) {
        return true;
      }

      return false;
    });

    if (isAllowed) {
      return trimmedUrl;
    }

    // If not allowed, return fallback (or null if fallback is null)
    return fallback ?? null;
  } catch {
    // If URL parsing fails, treat as invalid and return fallback (or null if fallback is null)
    return fallback ?? null;
  }
}
